import React, { useState, useEffect, createContext } from "react";
import getKeycloak from "./keycloak";
import { setupAxiosInterceptors } from "./api/axiosConfig";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    const initKeycloak = async () => {
      const keycloakInstance = getKeycloak();

      try {
        // Initialize with a minimum validity and enable silent check-sso
        const authenticated = await keycloakInstance.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri:
            window.location.origin + "/silent-check-sso.html",
          pkceMethod: "S256",
          checkLoginIframe: true,
        });

        setKeycloak(keycloakInstance);
        setAuthenticated(authenticated);

        if (authenticated) {
          setUser(keycloakInstance.tokenParsed);
          setupAxiosInterceptors(keycloakInstance);

          // Set up token refresh - refresh when token is at 70% of its lifetime
          const tokenRefreshInterval = setInterval(() => {
            keycloakInstance
              .updateToken(30)
              .then((refreshed) => {
                if (refreshed) {
                  setUser(keycloakInstance.tokenParsed);
                }
              })
              .catch(() => {
                console.error("Failed to refresh token");
                // Consider logging out the user here if the refresh consistently fails
                // keycloakInstance.logout();
              });
          }, 60 * 1000); // Check every minute

          setRefreshInterval(tokenRefreshInterval);
        }

        setInitialized(true);
      } catch (error) {
        console.error("Authentication initialization error:", error);
        setInitialized(true);
      }
    };

    initKeycloak();

    // Clean up interval on unmount
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  // Clear refresh interval when user is no longer authenticated
  useEffect(() => {
    if (!authenticated && refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [authenticated, refreshInterval]);

  // Re-export logout with cleanup
  const logout = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
    keycloak.logout();
  };

  if (!initialized) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ keycloak, authenticated, user, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
